import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Typography, Stack, IconButton, Button, CircularProgress, Divider } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { StyledTooltip } from '@/modules/common/components/StyledTooltip';
import { usePrevious } from '@/modules/common/hooks';
import { ConfirmDialog } from '@/modules/common/components/ConfirmDialog';
import { assertNever } from '@/helpers/utils';
import { DeleteIcon, FitToScreenIcon, InfoIcon } from '@/assets/icons';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { useFloorplanDesignStore } from '@/modules/floorplanDesign/store/useFloorplanDesignStore';
import { METERS_TO_MILLIMETERS_FACTOR } from '@/modules/canvas/factories/element';
import { AppServiceFactory } from '@/modules/app/service/AppServiceFactory';
import { useCanvasStore } from '@/modules/canvas/store';
import { logger } from '@common/logging/logger';
import { CanvasServiceFactory } from '@/modules/canvas/services/Canvas/CanvasServiceFactory';
import { IsLatestRevisionSelector } from '@/selectors';
import { FileUploadButtonWithSubtext } from '@/modules/common/components/FileUploadButtonWithSubtext';

import { ReferenceImageServiceFactory } from '../../service';
import { useReferenceImageUIStore } from '../../store';
import {
  REFERENCE_SCALE_MINIMUM_DISTANCE,
  REFERENCE_IMAGE_ELEMENT_ID,
  REFERENCE_IMAGE_WARNING_SIZE_BYTES,
  REFERENCE_IMAGE_UPLOAD_MAX_SIZE_BYTES,
} from '../../constants';
import { ReferenceImageControls } from './ReferenceImageControls';
import { DistanceInput } from './DistanceInput';
import { CoordinatesInput } from './CoordinatesInput';

export const ReferencePropertiesComponent = () => {
  const { t } = useTranslation();
  const { showSnackbar } = useSnackbarStore();
  const isLatest = IsLatestRevisionSelector.useReactive();
  const [deleteReferenceOpened, setDeleteReferenceOpened] = useState(false);
  const { referenceImage, referenceImageActions } = useReferenceImageUIStore();
  const fddmReferenceImage = useFloorplanDesignStore((state) => state.uxonly.referenceImage);
  const previousUploadStatus = usePrevious(referenceImage.uploadProcess.status);
  const referenceImageSvc = useMemo(
    () => ReferenceImageServiceFactory.getInstance().createService(),
    [],
  );
  const appSvc = useMemo(() => AppServiceFactory.getInstance().createAppService(), []);
  const canvasSvc = useMemo(() => CanvasServiceFactory.getInstance().createCanvasService(), []);

  const onScaleClick = useCallback(() => {
    canvasSvc.setCursor('cross')
    appSvc.activateTool('scale_reference');
  }, [appSvc, canvasSvc]);

  const onConfirmScaleClick = useCallback(() => {
    const { originalDimensions, scaleDraft } = referenceImage;

    if (!scaleDraft) {
      logger.error('scaleDraft not defined');
      return;
    }
    if (!originalDimensions) {
      logger.error('originalDimensions not defined');
      return;
    }

    if (!scaleDraft.pointA || !scaleDraft.pointB) {
      showSnackbar(t('errors:reference.points_not_set'));
      return;
    }
    if (
      scaleDraft.distanceBetweenPoints === null ||
      scaleDraft.distanceBetweenPoints < REFERENCE_SCALE_MINIMUM_DISTANCE
    ) {
      showSnackbar(t('errors:reference.invalid_distance'));
      return;
    }

    referenceImageSvc.scaleReferenceImage({
      canvasPointA: scaleDraft.pointA,
      canvasPointB: scaleDraft.pointB,
      realDistanceBetweenPointsInMm: scaleDraft.distanceBetweenPoints,
    });
  }, [referenceImage, showSnackbar, t, referenceImageSvc]);

  const onScaleCancelClick = useCallback(() => {
    appSvc.resetTool();
  }, [appSvc]);

  const onMoveClick = useCallback(() => {
    appSvc.activateTool('move_reference');
  }, [appSvc]);

  const onMoveConfirmClick = useCallback(async () => {
    const canvas = useCanvasStore.getState().instance;
    if (!canvas) return;

    const referenceElement = canvas.getElement(REFERENCE_IMAGE_ELEMENT_ID);

    if (!referenceElement) {
      logger.error('Could not find reference image element in the canvas');
      return;
    }

    if (referenceElement.type !== 'ImageCentered' || !referenceElement?.position) {
      logger.error('Exepcted reference image element to have a defined position.');
      return;
    }

    const currentPositionInCanvas = referenceElement.position;

    referenceImageSvc.moveReferenceImage(
      {
        centerX: currentPositionInCanvas.x / METERS_TO_MILLIMETERS_FACTOR,
        centerY: currentPositionInCanvas.y / METERS_TO_MILLIMETERS_FACTOR,
      },
      false,
    );
    referenceImageSvc.endMovingLifecycle();
  }, [referenceImageSvc]);

  const onMoveCancelClick = useCallback(() => {
    referenceImageSvc.endMovingLifecycle();
  }, [referenceImageSvc]);

  const onUpload = useCallback(
    (file: File) => {
      if (
        file.size > REFERENCE_IMAGE_WARNING_SIZE_BYTES &&
        file.size <= REFERENCE_IMAGE_UPLOAD_MAX_SIZE_BYTES
      ) {
        showSnackbar(t('interface:reference.large_image_load_warning'));
      }

      referenceImageSvc.uploadReferenceImage(file);
    },
    [referenceImageSvc, showSnackbar, t],
  );

  const onDistanceChange = useCallback(
    (newValue: number) => {
      if (newValue < REFERENCE_SCALE_MINIMUM_DISTANCE) {
        return {
          error: t('common:minimum_value', {
            minValue: REFERENCE_SCALE_MINIMUM_DISTANCE,
          }),
        };
      }

      referenceImageActions.setScaleDraft((current) => {
        if (current === null) {
          // Keep TS happy
          return null;
        }

        return {
          ...current,
          distanceBetweenPoints: newValue,
        };
      });
    },
    [referenceImageActions, t],
  );

  const setOpacity = useCallback(
    (value: number): void => {
      referenceImageSvc.updateReferenceImageOpacity(value);
    },
    [referenceImageSvc],
  );

  const onDeleteBtnClick = useCallback(() => {
    setDeleteReferenceOpened(true);
  }, []);

  const onInfoBtnClick = useCallback(() => {
    referenceImageActions.setRequiredScalingDialogOpenState(true);
  }, [referenceImageActions]);

  const closeDeleteConfirmDialog = useCallback(() => {
    setDeleteReferenceOpened(false);
  }, []);

  const onDeleteConfirmClick = useCallback(() => {
    referenceImageSvc.deleteReferenceImage();
    closeDeleteConfirmDialog();
  }, [closeDeleteConfirmDialog, referenceImageSvc]);

  const zoomToFitReference = useCallback(() => {
    CanvasServiceFactory.getInstance()
      .createCanvasService()
      .zoomToFitElements([REFERENCE_IMAGE_ELEMENT_ID]);
  }, []);

  // Show upload result message
  useEffect(() => {
    if (referenceImage.uploadProcess.status === 'error') {
      switch (referenceImage.uploadProcess.code) {
        case 'incompatible_file':
          showSnackbar(t('errors:reference.incompatible_file'));
          break;
        case 'not_an_image':
          showSnackbar(t('errors:reference.not_an_image'));
          break;
        case 'save_failed':
          showSnackbar(t('errors:reference.failed_to_save'));
          break;
        case 'image_processing_failed':
          showSnackbar(t('errors:reference.image_processing_failed'));
          break;
        case 'file_too_large':
          showSnackbar(t('errors:reference.upload_file_too_large'));
          break;
        default:
          assertNever(referenceImage.uploadProcess.code);
      }
    }
  }, [previousUploadStatus, referenceImage.uploadProcess, showSnackbar, t]);

  const controlsDisabled = useMemo(
    () => !isLatest || referenceImage.uploadProcess.status === 'loading',
    [isLatest, referenceImage.uploadProcess.status],
  );

  const distanceDisabled =
    !isLatest ||
    referenceImage.mode !== 'scale' ||
    !referenceImage.scaleDraft ||
    !referenceImage.scaleDraft.pointA ||
    !referenceImage.scaleDraft.pointB;

  return (
    <>
      {deleteReferenceOpened && (
        <ConfirmDialog
          title={t('interface:setup.reference.remove.title')}
          content={t('interface:setup.reference.remove.description')}
          yesLabel={t('common:confirm')}
          noLabel={t('common:cancel')}
          open
          onClose={closeDeleteConfirmDialog}
          onConfirm={onDeleteConfirmClick}
          onDecline={closeDeleteConfirmDialog}
        />
      )}
      <Stack px={2} gap={2} data-testid='ReferenceProperties'>
        <Stack direction='row' alignItems='center' justifyContent='space-between'>
          <Typography
            sx={{
              fontSize: '14px',
            }}
          >
            {t('interface:setup.reference.properties_title')}
          </Typography>
          <Stack direction='row' spacing={1}>
            {fddmReferenceImage && (
              <StyledTooltip title={t('interface:zoom_to_fit')}>
                <IconButton
                  onClick={zoomToFitReference}
                  sx={{ p: 0, width: '24px', height: '24px' }} 
                >
                  <FitToScreenIcon />
                </IconButton>
              </StyledTooltip>
            )}
            <StyledTooltip title='Open Explanation'>
              <IconButton onMouseDown={onInfoBtnClick} sx={{ p: 0, width: '24px', height: '24px' }}>
                <InfoIcon />
              </IconButton>
            </StyledTooltip>
            {fddmReferenceImage && (
              <StyledTooltip title='Delete Reference' disabled={controlsDisabled}>
                <IconButton
                  disabled={controlsDisabled}
                  onMouseDown={onDeleteBtnClick}
                  sx={{ p: 0, width: '24px', height: '24px' }}
                >
                  <DeleteIcon />
                </IconButton>
              </StyledTooltip>
            )}
          </Stack>
        </Stack>

        {(referenceImage.uploadProcess.status === 'idle' ||
          referenceImage.uploadProcess.status === 'error') && (
          <FileUploadButtonWithSubtext
            label={t('interface:reference.upload_modal.label')}
            subText={t('interface:reference.upload_model.formats_text')}
            accept='image/*'
            onUploaded={onUpload}
            disabled={controlsDisabled}
          />
        )}

        {referenceImage.uploadProcess.status === 'loading' && !fddmReferenceImage && (
          <Stack direction='row' alignItems='center' gap={1}>
            <Typography variant='body2'>
              {t('interface:reference.upload_modal.loading_text')}
            </Typography>
            <CircularProgress size={18} />
          </Stack>
        )}

        {fddmReferenceImage ? (
          <ReferenceImageControls
            referenceMode={referenceImage.mode}
            referenceFileName={referenceImage.fileDisplayName}
            onScaleClick={onScaleClick}
            onScaleCancelClick={onScaleCancelClick}
            onMoveClick={onMoveClick}
            onMoveCancelClick={onMoveCancelClick}
            onMoveConfirmClick={onMoveConfirmClick}
            isLoading={referenceImage.uploadProcess.status === 'loading'}
            disabled={controlsDisabled}
            opacity={fddmReferenceImage.opacity}
            setOpacity={setOpacity}
          />
        ) : null}

        {referenceImage.scaleDraft && (
          <>
            <Divider />
            <CoordinatesInput
              disabled={distanceDisabled}
              pointA={referenceImage.scaleDraft.pointA}
              pointB={referenceImage.scaleDraft.pointB}
            />
            <DistanceInput
              disabled={distanceDisabled}
              value={referenceImage.scaleDraft.distanceBetweenPoints || 0}
              onChange={onDistanceChange}
            />
          </>
        )}

        {referenceImage.mode === 'scale' && (
          <Button onClick={onConfirmScaleClick} type='submit'>
            {t('interface:reference.properties.confirm_scale')}
          </Button>
        )}
      </Stack>
    </>
  );
};

export const ReferenceProperties = memo(ReferencePropertiesComponent);
